import React, { useCallback, useRef, useState } from 'react';
import axios from 'axios';
import { Paperclip, FileText } from 'lucide-react';
import { useI18n } from '../../utils/i18n';
import AnhangViewer from '../ui/AnhangViewer';
import { anhangArt, istAudio } from '../../utils/anhangArt';
import AudioAnhang from '../ui/AudioAnhang';
import '../../styles/MailAnhaenge.css';

// #635: Anhaenge der verknuepften Mail direkt am Verlaufseintrag, damit man
// sie am Ticket sieht, ohne die Mail zu oeffnen. Die Auswahl (ohne
// Signaturbilder) trifft der Server (server/utils/mailAnhangFilter.js).
// Der Link laedt die Datei ueber die bestehende Route herunter; das
// funktioniert auch mobil, weil der Browser selbst navigiert (kein
// window.open nach einem await).
export const mailAnhangUrl = (anhang) =>
  `/api/emails/${encodeURIComponent(anhang.emailId)}/attachments/${encodeURIComponent(anhang.id)}/content`;

// #636: Bilder und PDFs der Mail öffnen im AnhangViewer; die Anhänge
// derselben Mail sind die Gruppe für < >. Alles andere bleibt Download.
export const mailAnhangImViewer = (a) => ({
  id: `${a.emailId}-${a.id}`,
  name: a.name,
  typ: a.contentType,
  laden: () => axios.get(mailAnhangUrl(a), { responseType: 'blob' }).then(r => r.data),
  downloadUrl: mailAnhangUrl(a)
});

/**
 * Für die Container (Desktop-Historie, Mobilansicht, Mail-Ansicht):
 * `oeffnenFuer(liste)` liefert das `onOeffnen` einer MailAnhaenge-Liste,
 * `viewer` ist das Element, das der Container irgendwo rendert.
 *
 * Optional `beimOeffnen`/`beimSchliessen`: Die Mobilansicht hängt damit einen
 * Eintrag in den Verlauf (`?ma=1`), damit die Zurück-Taste nur den Betrachter
 * schließt und nicht das Ticket. `beimSchliessen` ersetzt dann den
 * Schließen-Knopf; geschlossen wird über `schliessen()`, sobald der Eintrag
 * weg ist. `offen` sagt dem Container, ob gerade etwas angezeigt wird.
 */
export function useMailAnhangViewer(optionen = {}) {
  const [offen, setOffen] = useState(null);
  const optionenRef = useRef(optionen);
  optionenRef.current = optionen;
  const oeffnenFuer = useCallback((liste) => (anhang) => {
    const zeigbar = (Array.isArray(liste) ? liste : []).filter(x => anhangArt(x));
    const i = zeigbar.findIndex(x => x.emailId === anhang.emailId && x.id === anhang.id);
    if (i < 0) return false;
    setOffen({ anhaenge: zeigbar.map(mailAnhangImViewer), start: i });
    if (optionenRef.current.beimOeffnen) optionenRef.current.beimOeffnen();
    return true;
  }, []);
  const schliessen = useCallback(() => setOffen(null), []);
  const knopfSchliesst = useCallback(() => {
    if (optionenRef.current.beimSchliessen) optionenRef.current.beimSchliessen();
    else setOffen(null);
  }, []);
  const viewer = offen
    ? <AnhangViewer anhaenge={offen.anhaenge} startIndex={offen.start} onClose={knopfSchliesst} />
    : null;
  return { oeffnenFuer, viewer, offen: !!offen, schliessen };
}

const groesse = (bytes) => {
  const n = Number(bytes) || 0;
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
};

// #508: Audio-Anhänge bekommen einen Player. `onTranskribieren(anhang)` setzt
// der Container nur, wenn es „Transkribieren“ geben soll (Schalter, Recht,
// Ticket); die Mail-Ansicht ohne Ticket lässt es weg.
const MailAnhaenge = ({ anhaenge, onOeffnen, onTranskribieren }) => {
  const { t } = useI18n();
  if (!Array.isArray(anhaenge) || anhaenge.length === 0) return null;
  return (
    <div className="mail-anhaenge">
      <div className="mail-anhaenge-titel">
        <Paperclip size={14} aria-hidden="true" />
        {t('ticketDetail.mailAttachments', 'Anhänge der E-Mail')} ({anhaenge.length})
      </div>
      <ul className="mail-anhaenge-liste">
        {anhaenge.map(a => (
          <li key={`${a.emailId}-${a.id}`}>
            <a
              className="mail-anhang"
              href={mailAnhangUrl(a)}
              onClick={(e) => {
                e.stopPropagation();
                if (onOeffnen && onOeffnen(a) === true) e.preventDefault();
              }}
            >
              <FileText size={14} aria-hidden="true" />
              <span className="mail-anhang-name">{a.name}</span>
              <span className="mail-anhang-groesse">{groesse(a.size)}</span>
            </a>
            {istAudio(a) && (
              <AudioAnhang
                anhang={a}
                laden={() => axios.get(mailAnhangUrl(a), { responseType: 'blob' }).then(r => r.data)}
                onTranskribieren={onTranskribieren ? () => onTranskribieren(a) : undefined}
              />
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default MailAnhaenge;
